/**
 * ── Vehicle label ────────────────────────────────────────────────────────────
 *
 * One rule for what a vehicle is called on screen (the backend mirror lives in
 * `FlowCargo-Subdomain-Based-Backend/utils/vehicleLabel.js` — keep the two in
 * step): the fleet's own name for it ("OPT 39") when one is set, otherwise its
 * plate.
 *
 * Screens only. Documents that identify the vehicle to someone outside the
 * fleet — the CMR, delivery note, group manifest, waybill — keep the plate,
 * because "OPT 39" means nothing to a border guard or a consignee.
 */

export interface LabelledVehicle {
  registrationNumber?: string | null;
  displayName?: string | null;
}

/** The vehicle's display name when it has one, otherwise its plate. */
export const vehicleLabel = (vehicle: LabelledVehicle | null | undefined): string =>
  vehicle?.displayName?.trim() || vehicle?.registrationNumber || "";

/**
 * The plate, when the label is not already showing it — for the second line
 * of a picker row, so "OPT 39" can still be told apart by its plate.
 */
export const vehiclePlateHint = (vehicle: LabelledVehicle | null | undefined): string =>
  vehicle?.displayName?.trim() ? (vehicle.registrationNumber ?? "") : "";

/**
 * A shipment's or group's truck/trailer field as screens show it. The field
 * holds either the populated vehicle or, for one typed in freely, the plate
 * text itself.
 */
export const vehicleRefLabel = (
  ref: LabelledVehicle | string | null | undefined,
): string => (typeof ref === "string" ? ref : vehicleLabel(ref));
